<script lang="ts" setup>
import { OtIcon } from '@1trading/design-system';
import type { PropType } from 'vue';

import type { BalanceModel } from '@exchange/libs/balances/service/src';
import XLogo from '@exchange/libs/components/x-components/src/lib/XLogo.vue';
import { marginService } from '@exchange/libs/futures/service/src';
import { CURRENCY_ELIGIBLE_FOR_MARGIN } from '@exchange/libs/futures/service/src/lib/constants.config';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import BalancesInfoMessageBlock from './BalancesInfoMessageBlock.vue';

defineProps({
  authenticatedUser: { type: Boolean, required: true },
  historySource: { type: Object as PropType<HistoryDataSource>, required: true },
  sortedList: { type: Array as PropType<BalanceModel[]>, required: true },
});

const emit = defineEmits<{
  (e: 'search:search-start'): void;
  (e: 'search:search-end'): void;
  (e: 'deposit', currencyCode?: string): void;
  (e: 'transfer', currencyCode: string): void;
}>();

const { getCurrencyName } = currencyService;
const { balance: marginBalance, equity, marginUsedPercentage } = marginService;
</script>

<template>
  <div class="balances-list-exchange-mobile">
    <balances-info-message-block
      v-if="!sortedList.length || !authenticatedUser"
      :authenticated-user="authenticatedUser"
    />
    <div
      v-else
      class="balances-list-exchange-mobile__content"
    >
      <div
        v-for="balance in sortedList"
        :key="balance.currencyCode"
        class="balance-item-mobile"
      >
        <div class="balance-item-mobile__header">
          <div class="balance-item-mobile__currency-info">
            <x-logo
              class="balance-item-mobile__icon"
              :name="balance.currencyCode"
              type="crypto"
            />
            <div class="balance-item-mobile__code-wrapper">
              <span class="balance-item-mobile__code">{{ balance.currencyCode }}</span>
              <span class="balance-item-mobile__name">{{ getCurrencyName(balance.currencyCode) }}</span>
            </div>
          </div>
          <div class="balance-item-mobile__actions">
            <x-button
              class="balance-item-mobile__action-button"
              size="3xs"
              variant="tertiary"
              @click="() => emit('deposit', balance.currencyCode)"
            >
              <ot-icon name="add-square" />
            </x-button>
            <x-button
              class="balance-item-mobile__action-button"
              size="3xs"
              variant="tertiary"
              @click="() => emit('transfer', balance.currencyCode)"
            >
              <ot-icon name="arrow-swap-horizontal" />
            </x-button>
          </div>
        </div>
        <div class="balance-item-mobile__details">
          <div class="balance-item-mobile__column">
            <div class="balance-item-mobile__row">
              <span class="balance-item-mobile__label">Net:</span>
              <span class="balance-item-mobile__value">
                <span>{{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? equity.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.total }}</span>
                /
                <ot-number-display
                  class="balance-item-mobile__value"
                  :grouping="true"
                  prefix="€"
                  :number="balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? equity : balance.totalFiatValue"
                  :precision="2"
                />
              </span>
            </div>
            <div class="balance-item-mobile__row">
              <span class="balance-item-mobile__label">{{ $t('modules.myOrders.header.availableMargin') }}:</span>
              <span class="balance-item-mobile__value">{{
                balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginBalance.available.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.available
              }}</span>
            </div>
          </div>
          <div class="balance-item-mobile__column">
            <div class="balance-item-mobile__row">
              <span class="balance-item-mobile__label">{{ $t('modules.myOrders.header.usedMargin') }}:</span>
              <span class="balance-item-mobile__value">{{
                balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginBalance.used.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.locked
              }}</span>
            </div>
            <div class="balance-item-mobile__row">
              <span class="balance-item-mobile__label">{{ $t('modules.myOrders.header.usedForMargin') }}:</span>
              <span class="balance-item-mobile__value">{{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginUsedPercentage + '%' : '0%' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */

.balances-list-exchange-mobile {
  display: flex;
  flex-direction: column;
}

.balance-item-mobile {
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);
  background-color: rgb(var(--v-theme-elevation-1));

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__currency-info {
    display: flex;
    align-items: center;
  }

  &__icon {
    width: 32px;
    height: 32px;
    margin-right: 12px;
  }

  &__code-wrapper {
    display: flex;
    flex-direction: column;
  }

  &__code {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: rgb(var(--v-theme-text-primary));
  }

  &__name {
    font-size: var(--font-size-sm);
    color: rgb(var(--v-theme-text-secondary));
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__action-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: rgb(var(--v-theme-text-primary));
    cursor: pointer;
  }

  &__details {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  &__column {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  &__row {
    display: flex;
    justify-content: space-between;
  }

  &__label {
    font-size: var(--font-size-sm);
    color: rgb(var(--v-theme-text-secondary));
  }

  &__value {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: rgb(var(--v-theme-text-primary));
  }
}
</style>
